import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { fullName, useFunnelStages, useStage } from "../funnel/hooks";
import { FunnelShell, StageBadge } from "../funnel/shared";
import type { CrmDataProvider } from "../providers/types";
import {
  PERIODS,
  percent,
  periodStart,
  reactionParts,
  type Period,
} from "./dashboardLogic";

/** Owner dashboard (docs/design/Dashboard, DashboardM; ТЗ "Дашборд власника") */
export const OwnerDashboard = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const stages = useFunnelStages();
  const stage = useStage();
  const [period, setPeriod] = useState<Period>("week");
  const stageValues = stages.map((s) => s.value);
  const { data } = useQuery({
    queryKey: ["owner_dashboard", period, stageValues],
    queryFn: () =>
      dataProvider.ownerDashboard(periodStart(period, new Date()), stageValues),
  });

  const periodTabs = (
    <div
      role="tablist"
      className={cn(
        "grid grid-cols-3 rounded-xl border bg-card p-1 text-sm",
        isMobile ? "w-full" : "ml-auto",
      )}
    >
      {PERIODS.map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={period === value}
          onClick={() => setPeriod(value)}
          className={cn(
            "rounded-lg px-4 py-1.5 font-semibold",
            period === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground",
          )}
        >
          {translate(`crm.owner_dashboard.periods.${value}`)}
        </button>
      ))}
    </div>
  );

  const reaction = reactionParts(data?.reaction_minutes ?? null);
  const call5 = data ? percent(data.called_5, data.leads) : null;
  const call15 = data ? percent(data.called_15, data.leads) : null;
  const kpis: { label: string; value: string; hint: string; tone?: string }[] =
    [
      {
        label: translate("crm.owner_dashboard.reaction"),
        value: reaction
          ? `${reaction.count} ${translate(`crm.funnel.units.${reaction.unit}`)}`
          : "—",
        hint: translate("crm.owner_dashboard.reaction_hint"),
      },
      {
        label: translate("crm.owner_dashboard.call_5"),
        value: call5 == null ? "—" : `${call5}%`,
        hint: translate("crm.owner_dashboard.call_15", {
          value: call15 == null ? "—" : `${call15}%`,
        }),
      },
      {
        label: translate("crm.owner_dashboard.no_action"),
        value: String(data?.no_action_24h ?? "—"),
        hint: translate("crm.owner_dashboard.no_action_hint"),
        tone: data?.no_action_24h ? "text-destructive" : undefined,
      },
      {
        label: translate("crm.owner_dashboard.overdue"),
        value: String(data?.overdue_tasks ?? "—"),
        hint: translate("crm.owner_dashboard.overdue_hint"),
        tone: data?.overdue_tasks ? "text-warning" : undefined,
      },
    ];

  const top = data?.funnel[0]?.count ?? 0;

  return (
    <FunnelShell title={translate("ra.page.dashboard")} actions={periodTabs}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-2xl border bg-card p-4">
            <div className="text-sm text-muted-foreground">{kpi.label}</div>
            <div
              className={cn("text-3xl font-extrabold tracking-tight", kpi.tone)}
            >
              {kpi.value}
            </div>
            <div className="text-xs text-muted-foreground">{kpi.hint}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_440px]">
        <section className="rounded-2xl border bg-card p-5">
          <h2 className="mb-4 text-lg font-bold">
            {translate("crm.owner_dashboard.funnel")}
          </h2>
          <div className="flex flex-col gap-3">
            {data?.funnel.map((row, index) => {
              const s = stage(row.stage);
              const previous = index > 0 ? data.funnel[index - 1].count : null;
              const share =
                previous != null ? percent(row.count, previous) : null;
              return (
                <div
                  key={row.stage}
                  className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-sm sm:grid-cols-[100px_1fr_80px]"
                >
                  <span className="truncate">{s?.label ?? row.stage}</span>
                  <div className="h-5 overflow-hidden rounded-md bg-muted">
                    <div
                      className="h-full rounded-md"
                      style={{
                        width: `${top ? Math.max(2, (100 * row.count) / top) : 0}%`,
                        backgroundColor: s?.color,
                      }}
                    />
                  </div>
                  <span className="text-right font-bold tabular-nums">
                    {row.count}
                    {share != null ? ` · ${share}%` : ""}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {translate("crm.owner_dashboard.funnel_hint")}
          </p>
        </section>

        {isMobile ? null : (
          <section className="rounded-2xl border bg-card p-5">
            <h2 className="mb-2 text-lg font-bold">
              {translate("crm.owner_dashboard.warm")}
            </h2>
            {data?.warm.length ? (
              <ul className="divide-y">
                {data.warm.map((lead) => (
                  <li key={lead.id} className="py-3">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/funnel/${lead.id}`} className="font-bold">
                        {fullName(lead)}
                      </Link>
                      <StageBadge stage={stage(lead.stage)} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[
                        lead.answered &&
                          translate("crm.owner_dashboard.rules.answered"),
                        lead.viewed &&
                          translate("crm.owner_dashboard.rules.viewed"),
                        lead.repeat_contact &&
                          translate("crm.owner_dashboard.rules.repeat"),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {translate("crm.owner_dashboard.no_warm")}
              </p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {translate("crm.owner_dashboard.warm_hint")}
            </p>
          </section>
        )}
      </div>

      {isMobile ? null : (
        <section className="overflow-x-auto rounded-2xl border bg-card">
          <h2 className="px-5 pt-5 text-lg font-bold">
            {translate("crm.owner_dashboard.sources")}
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">
                  {translate("crm.owner_dashboard.source")}
                </th>
                <th className="px-5 py-3 text-right">
                  {translate("crm.owner_dashboard.source_leads")}
                </th>
                <th className="px-5 py-3 text-right">
                  {translate("crm.owner_dashboard.source_viewings")}
                </th>
                <th className="px-5 py-3 text-right">
                  {translate("crm.owner_dashboard.source_won")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.sources.map((row) => (
                <tr key={row.source} className="border-b last:border-0">
                  <td className="px-5 py-2 font-semibold">
                    {translate(`resources.contacts.sources.${row.source}`)}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {row.leads}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {row.viewings}
                    {row.leads ? ` · ${percent(row.viewings, row.leads)}%` : ""}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {row.won}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </FunnelShell>
  );
};
